import { EventEmitter } from 'events';

interface UseWebSocketProps {
  url: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  connectionTimeout?: number;
  maxExponentialBackoff?: number; // 添加最大指数退避时间
}

interface WebSocketMessage {
  type: string;
  event?: string;
  cmd?: string;
  data: any; // 可以改为更具体的类型或使用泛型来增加类型安全性
}

export interface WebSocketClientInstance extends EventEmitter {
  start: () => void;
  stop: () => void;
  send: (message: WebSocketMessage) => void;
}

export class WebSocketClient extends EventEmitter {
  private socket: WebSocket | null = null;
  private url: string;
  private shouldReconnect = true; // 区分手动断开时不要重连
  private reconnectInterval: number;
  private maxReconnectAttempts: number;
  private maxExponentialBackoff: number; // 最大指数退避时间
  private connectionTimeout: number;
  private reconnectAttempts = 0;
  private heartbeatTimer?: number; // 更正类型为number
  private connectionTimer?: number; // 更正类型为number

  constructor({
    connectionTimeout = 10000,
    maxExponentialBackoff = 32000,
    maxReconnectAttempts = 10,
    reconnectInterval = 5000, // 默认最大指数退避时间
    url,
  }: UseWebSocketProps) {
    super();
    this.url = url;
    this.reconnectInterval = reconnectInterval;
    this.maxReconnectAttempts = maxReconnectAttempts;
    this.maxExponentialBackoff = maxExponentialBackoff;
    this.connectionTimeout = connectionTimeout;
  }

  private connect = () => {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('error', new Error('Max reconnect attempts reached'));
      return;
    }

    this.socket = new WebSocket(this.url);

    this.connectionTimer = window.setTimeout(() => {
      // 更正为window.setTimeout
      this.emit('error', new Error('Connection timeout'));
      this.socket?.close();
    }, this.connectionTimeout);

    this.socket.onopen = () => {
      window.clearTimeout(this.connectionTimer); // 更正为window.clearTimeout
      this.emit('open');
      this.reconnectAttempts = 0;
      this.shouldReconnect = true;
      this.startHeartbeat();
    };

    this.socket.onmessage = (event) => {
      if (typeof event.data === 'string') {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.emit('message', message);
      }
    };

    this.socket.onerror = (error) => {
      window.clearTimeout(this.connectionTimer); // 更正为window.clearTimeout
      this.emit('error', error);
    };

    this.socket.onclose = (event) => {
      window.clearTimeout(this.connectionTimer); // 更正为window.clearTimeout
      this.emit('close', event);
      this.startReconnect();
    };
  };

  private startReconnect = () => {
    if (this.shouldReconnect) {
      const exponentialBackoff = Math.min(
        this.reconnectInterval * Math.pow(2, this.reconnectAttempts),
        this.maxExponentialBackoff
      );
      window.setTimeout(() => {
        this.reconnectAttempts++;
        this.connect();
      }, exponentialBackoff);
    }
  };

  private disconnect = () => {
    window.clearInterval(this.heartbeatTimer); // 更正为window.clearInterval
    window.clearTimeout(this.connectionTimer); // 更正为window.clearTimeout
    this.reconnectAttempts = 0; // 重置尝试次数
    // 重置shouldReconnect，防止自动重连
    this.shouldReconnect = false;
    this.socket?.close();
  };

  private sendMessage = (message: WebSocketMessage) => {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      this.emit('error', new Error('WebSocket is not open'));
    }
  };

  private startHeartbeat = () => {
    window.clearInterval(this.heartbeatTimer); // 更正为window.clearInterval
    this.heartbeatTimer = window.setInterval(() => {
      // 更正为window.setInterval
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.sendMessage({ type: 'request', cmd: 'ping', data: {} });
      }
    }, 20 * 1000);
  };

  public start = () => {
    window.addEventListener('beforeunload', this.disconnect);
    this.connect();
  };

  public stop = () => {
    window.removeEventListener('beforeunload', this.disconnect);
    this.disconnect();
  };

  public send = (message: WebSocketMessage) => {
    this.sendMessage(message);
  };
}
